import { Injectable } from '@angular/core';
import { GqlServiceV2 } from '../../content/service/gql.service-v2';
import { CollectionGridObject, CollectionObject, OkObject } from '@it2go/types';
import { CollectionImportInput } from '@libs/types/src/chcrm/input-type/collection/collection-import.input';
import { CollectionDeleteInput } from '@libs/types/src/chcrm/input-type/collection/collection-delete.input';
import { GridInput } from '@it2go/data-grid';
import { filterFrag } from '../../../services/types/filter.types';
import { collectionDonorsStatistic, collectionFrag, collectionGridItemFrag, collectionStatistic, collectionStatistics } from '../../../services/types/collection.types';
import { CollectionCreateInput } from '@libs/types/src/chcrm/input-type/collection/collection-create.input';
import { CollectionUpdateInput } from '@libs/types/src/chcrm/input-type/collection/collection-update.input';
import { CollectionGetInput } from '@libs/types/src/chcrm/input-type/collection/collection-get.input';
import {
    CollectionGetStatisticsInput, CollectionsGetStatisticsInput,
} from '@libs/types/src/chcrm/input-type/collection/collections-get-statistics.input';
import { CollectionStatisticObject, CollectionStatisticsObject } from '@libs/types/src/chcrm/object-type/collection/collection-statistics.object';
import {
    CollectionDonorsStatisticsObject,
} from '@libs/types/src/chcrm/object-type/collection/collection-donors-statistics.object';

@Injectable({ providedIn: 'root' })
export class CollectionGqlService extends GqlServiceV2 {

    deleteCollection(input: CollectionDeleteInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: CollectionDeleteInput!) {
                    collection {
                        delete(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getCollections(input: GridInput | null, frag: string = collectionGridItemFrag) {
        return this.query<CollectionGridObject>(
            `
                query($input: GridInput) {
                    collection {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public getSearchCollection(input: GridInput | null, frag: string = collectionGridItemFrag) {
        return this.query<CollectionGridObject>(
            `
                query($input: GridInput) {
                    collection {
                        filter(input: $input) {
                            ${filterFrag}
                            items {
                                ${frag}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    createCollection(input: CollectionCreateInput) {
        return this.mutate(
            `
                mutation($input: CollectionCreateInput!) {
                    collection {
                        create(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    updateCollection(input: CollectionUpdateInput) {
        return this.mutate(
            `
                mutation($input: CollectionUpdateInput!) {
                    collection {
                        update(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }

    getCollection(input: CollectionGetInput, frag: string = collectionFrag) {
        return this.query<CollectionObject>(
            `
                query($input: CollectionGetInput!) {
                    collection {
                        get(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getCollectionStatistics(input: CollectionGetStatisticsInput, frag: string = collectionStatistic) {
        return this.query<CollectionStatisticObject>(
            `
                query($input: CollectionGetStatisticsInput!) {
                    collection {
                        statistic(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getCollectionDonorsStatistic(input: CollectionGetStatisticsInput, frag: string = collectionDonorsStatistic) {
        return this.query<CollectionDonorsStatisticsObject[]>(
            `
                query($input: CollectionGetStatisticsInput!) {
                    collection {
                        donorsStatistic(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    getCollectionsStatistics(input: CollectionsGetStatisticsInput, frag: string = collectionStatistics) {
        return this.query<CollectionStatisticsObject[]>(
            `
                query($input: CollectionsGetStatisticsInput!) {
                    collection {
                        statistics(input: $input) {
                            ${frag}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public importCollectionCsv(input: CollectionImportInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: CollectionImportInput!) {
                    collection {
                        importCsv(input: $input) {
                            ok
                        }
                    }
                }
            `,
            { input },
        );
    }
}
